'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useFoundry } from '@/lib/foundry/context';
import { SECTION_CONFIG, type SectionKey, type PlanSection, type ExecutionPlanChartData } from '@/lib/foundry/types';
import * as api from '@/lib/foundry/api';
import { SectionRefinementModal } from './SectionRefinementModal';
import { MarkdownRenderer } from './MarkdownRenderer';

// Lazy load chart components to avoid SSR issues with Recharts
const FinancialProjectionsChart = dynamic(
  () => import('@/components/foundry/charts/FinancialProjectionsChart'),
  { ssr: false, loading: () => <div className="h-64 bg-background/40 animate-pulse rounded-lg" /> }
);
const MarketSizeChart = dynamic(
  () => import('@/components/foundry/charts/MarketSizeChart'),
  { ssr: false, loading: () => <div className="h-64 bg-background/40 animate-pulse rounded-lg" /> }
);
const FoundryChartRenderer = dynamic(
  () => import('@/components/foundry/charts/FoundryChartRenderer'),
  { ssr: false, loading: () => <div className="h-64 bg-background/40 animate-pulse rounded-lg" /> }
);
const ExecutionPlanSection = dynamic(
  () => import('@/components/foundry/ExecutionPlanSection'),
  { ssr: false, loading: () => <div className="h-96 bg-background/40 animate-pulse rounded-lg" /> }
);

interface SectionCardProps {
  sectionKey: SectionKey;
  section?: PlanSection;
  planId: string;
  onExpand: (key: SectionKey) => void;
  onRefine: (key: SectionKey) => void;
  onReload: () => void;
}

function SectionCard({ sectionKey, section, planId, onExpand, onRefine, onReload }: SectionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const { actions } = useFoundry();

  // Special handling for execution_plan section
  const isExecutionPlan = sectionKey === 'execution_plan';

  const config = SECTION_CONFIG[sectionKey];
  const hasContent = section?.content_md && section.content_md.length > 0;
  const hasExecutionPlanData = (section?.content_data?.execution_plan?.milestones?.length ?? 0) > 0;
  const hasIssues = section?.sanity_checks?.some((c) => !c.passed);
  const isProcessing = section?.status === 'processing';
  const isFailed = section?.status === 'failed';

  // Handle saving execution plan data
  const handleSaveExecutionPlan = async (data: ExecutionPlanChartData) => {
    try {
      await api.expandSection(planId, {
        section_key: sectionKey,
        additional_instructions: JSON.stringify({ execution_plan_data: data }),
      });
      onReload();
    } catch (error) {
      console.error('[Foundry] Failed to save execution plan:', error);
    }
  };

  // Determine if card is clickable (has content and not processing and not editing)
  // For execution_plan, check both markdown content and execution plan data
  const hasSectionContent = isExecutionPlan ? (hasContent || hasExecutionPlanData) : hasContent;
  const isClickable = hasSectionContent && !isProcessing && !isEditing && !isExecutionPlan;

  const handleEditToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing && section?.content_md) {
      setEditContent(section.content_md);
    }
    setIsEditing(!isEditing);
  };

  const handleSaveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Save the edited content via the API
    if (section && editContent !== section.content_md) {
      await actions.updatePlan({
        // Note: In a full implementation, you'd update the specific section
        // For now, we'll just toggle back to view mode
      });
    }
    setIsEditing(false);
  };

  return (
    <div
      className={`p-6 bg-background/40 border rounded-lg transition-colors ${
        isExecutionPlan ? 'md:col-span-2' : ''
      } ${
        isProcessing
          ? 'border-accent/40 animate-pulse'
          : isFailed
          ? 'border-red-500/40'
          : isClickable
          ? 'border-border-muted hover:border-border/30 cursor-pointer'
          : 'border-border-muted'
      }`}
      onClick={isClickable ? () => onRefine(sectionKey) : undefined}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{config.title}</h3>
          <div className="flex items-center gap-2 mt-1">
            {section?.status && (
              <span className={`text-xs ${
                section.status === 'processing' ? 'text-accent animate-pulse' :
                section.status === 'failed' ? 'text-red-400' :
                section.status === 'ai_draft' ? 'text-blue-400' :
                section.status === 'approved' ? 'text-green-400' :
                section.status === 'in_progress' ? 'text-accent' :
                'text-foreground-muted'
              }`}>
                {section.status === 'processing' ? 'generating...' :
                 section.status === 'failed' ? 'generation failed' :
                 section.status.replace('_', ' ')}
              </span>
            )}
            {hasIssues && !isProcessing && !isFailed && (
              <span className="text-xs text-yellow-400">• has issues</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {hasSectionContent && !isProcessing && !isExecutionPlan && (
            <>
              {/* View/Edit Toggle */}
              <button
                onClick={handleEditToggle}
                className={`px-3 py-2 border rounded-lg transition-colors text-sm ${
                  isEditing
                    ? 'border-accent text-accent'
                    : 'border-border text-foreground-secondary hover:border-border/40 hover:text-foreground'
                }`}
              >
                {isEditing ? 'View' : 'Edit'}
              </button>
              {/* Refine Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefine(sectionKey);
                }}
                className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:border-border/40 hover:text-foreground transition-colors text-sm"
              >
                Refine
              </button>
            </>
          )}
          {isProcessing ? (
            <div className="px-4 py-2 bg-accent-muted text-accent rounded-lg text-sm flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              Generating...
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExpand(sectionKey);
              }}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                isFailed
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-accent-muted text-accent hover:bg-accent/30'
              }`}
            >
              {isFailed ? 'Try Again' : hasSectionContent ? 'Regenerate' : 'Generate'}
            </button>
          )}
        </div>
      </div>

      {/* Special rendering for Execution Plan section */}
      {isExecutionPlan && section ? (
        <ExecutionPlanSection
          section={section}
          onSave={handleSaveExecutionPlan}
          onRegenerate={() => onExpand(sectionKey)}
        />
      ) : hasSectionContent ? (
        <div className="space-y-4">
          {/* Charts Section */}
          {section?.content_data && (
            <div className="space-y-4">
              {/* Financial Chart for financial_projections section */}
              {sectionKey === 'financial_projections' && section.content_data.financial && (
                <FinancialProjectionsChart data={section.content_data.financial} height={280} />
              )}

              {/* Market Chart for market_analysis section */}
              {sectionKey === 'market_analysis' && section.content_data.market && (
                <MarketSizeChart data={section.content_data.market} height={200} />
              )}

              {/* Generic charts from content_data.charts */}
              {section.content_data.charts && section.content_data.charts.length > 0 && (
                <div className="space-y-4">
                  {section.content_data.charts.map((chart, i) => (
                    <FoundryChartRenderer key={i} chart={chart} height={250} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Markdown Content / Editor */}
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full h-64 px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground text-sm font-mono placeholder-foreground-muted focus:outline-none focus:border-accent resize-y"
                placeholder="Enter markdown content..."
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(false);
                  }}
                  className="px-3 py-1.5 text-sm text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-3 py-1.5 bg-accent text-accent-foreground text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <MarkdownRenderer content={section?.content_md || ''} />
            </div>
          )}

          {/* Metrics Display */}
          {section?.content_data?.metrics && section.content_data.metrics.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4 border-t border-border-muted">
              {section.content_data.metrics.map((metric, i) => (
                <div key={i} className="p-3 bg-background/60 rounded-lg">
                  <div className="text-xs text-foreground-tertiary">{metric.label}</div>
                  <div className={`text-sm font-semibold ${
                    metric.status === 'good' ? 'text-green-400' :
                    metric.status === 'warning' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {metric.value}
                  </div>
                  {metric.benchmark && (
                    <div className="text-xs text-foreground-muted mt-1">{metric.benchmark}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-foreground-muted text-sm italic">
          Click Generate to create this section from your Lean Canvas
        </p>
      )}

      {section?.sanity_checks && section.sanity_checks.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border-muted">
          <h4 className="text-sm font-medium text-foreground-tertiary mb-2">Sanity Checks</h4>
          <div className="space-y-1">
            {section.sanity_checks.slice(0, 3).map((check, i) => (
              <div
                key={i}
                className={`text-xs flex items-center gap-2 ${
                  check.passed ? 'text-green-400' : check.severity === 'warning' ? 'text-yellow-400' : 'text-red-400'
                }`}
              >
                <span>{check.passed ? '✓' : '!'}</span>
                <span>{check.check}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SectionEditor() {
  const { state, actions } = useFoundry();
  const plan = state.currentPlan;
  const [refiningSection, setRefiningSection] = useState<SectionKey | null>(null);

  // Expansion is now async - just trigger it and let polling handle updates
  const handleExpand = async (sectionKey: SectionKey) => {
    await actions.expandSection(sectionKey);
    // Section status changes to 'processing' and UI updates automatically via polling
  };

  const handleRefine = (sectionKey: SectionKey) => {
    setRefiningSection(sectionKey);
  };

  const handleCloseRefine = () => {
    setRefiningSection(null);
  };

  // Check if any sections are currently processing
  const hasProcessingSections = plan?.sections?.some(s => s.status === 'processing') ?? false;

  if (!plan) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
      </div>
    );
  }

  const hasCanvas = plan.lean_canvas?.problem !== null;

  if (!hasCanvas) {
    return (
      <div className="p-6 bg-yellow-500/20 border border-yellow-500/40 rounded-lg text-center">
        <p className="text-yellow-300">
          Please complete your Lean Canvas first before expanding sections.
        </p>
      </div>
    );
  }

  const sectionKeys = Object.keys(SECTION_CONFIG) as SectionKey[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Business Plan Sections</h2>
        <button
          onClick={() => actions.runSanityCheck()}
          disabled={state.aiLoading || hasProcessingSections}
          className="px-4 py-2 border border-border text-foreground rounded-lg hover:border-border/40 disabled:opacity-50 transition-colors text-sm"
        >
          {state.aiLoading ? 'Running...' : 'Run Sanity Check'}
        </button>
      </div>

      {state.aiError && (
        <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
          {state.aiError}
        </div>
      )}

      {/* Sanity Check Results */}
      {state.sanityCheckResult && (
        <div className="p-6 bg-background/40 border border-border-muted rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Sanity Check Results</h3>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              state.sanityCheckResult.risk_level === 'low' ? 'bg-green-500/20 text-green-400' :
              state.sanityCheckResult.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
              state.sanityCheckResult.risk_level === 'high' ? 'bg-accent-muted text-accent' :
              'bg-red-500/20 text-red-400'
            }`}>
              Score: {state.sanityCheckResult.overall_score}/100
            </div>
          </div>

          {state.sanityCheckResult.critical_issues.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-400 mb-2">Critical Issues</h4>
              <ul className="space-y-1">
                {state.sanityCheckResult.critical_issues.map((issue, i) => (
                  <li key={i} className="text-sm text-foreground-secondary flex items-start gap-2">
                    <span className="text-red-400">!</span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state.sanityCheckResult.strengths.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-green-400 mb-2">Strengths</h4>
              <ul className="space-y-1">
                {state.sanityCheckResult.strengths.map((strength, i) => (
                  <li key={i} className="text-sm text-foreground-secondary flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Sections Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {sectionKeys.map((key) => {
          const section = plan.sections?.find((s) => s.section_key === key);
          return (
            <SectionCard
              key={key}
              sectionKey={key}
              section={section}
              planId={plan.id}
              onExpand={handleExpand}
              onRefine={handleRefine}
              onReload={() => actions.loadPlan(plan.id)}
            />
          );
        })}
      </div>

      {/* Refinement Modal */}
      {refiningSection && (
        <SectionRefinementModal
          sectionKey={refiningSection}
          section={plan.sections?.find((s) => s.section_key === refiningSection)}
          isOpen={!!refiningSection}
          onClose={handleCloseRefine}
        />
      )}
    </div>
  );
}
